"use client"
import React, { useRef, useState } from "react"
import Title from "../Title/Title"

export const CareerForm = () => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [resumeFileName, setResumeFileName] = useState<string | null>(null)
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [phone, setPhone] = useState("")
  const [linkedin, setLinkedin] = useState("")
  const [message, setMessage] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Simple sanitization helpers
  const sanitizeText = (str: string) => str.replace(/[<>]/g, "").trim()
  const sanitizeUrl = (str: string) => str.replace(/[<>]/g, "").trim()

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (fileInputRef.current && e.dataTransfer.files.length) {
      fileInputRef.current.files = e.dataTransfer.files
      setResumeFileName(e.dataTransfer.files[0].name)
    }
  }

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setResumeFileName(e.target.files[0].name)
    } else {
      setResumeFileName(null)
    }
  }

  const clearForm = () => {
    setFirstName("")
    setLastName("")
    setEmail("")
    setPhone("")
    setLinkedin("")
    setResumeFileName(null)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setMessage(null)

    // Basic client-side validation
    if (
      !firstName ||
      !lastName ||
      !email ||
      !phone ||
      !fileInputRef.current?.files?.length
    ) {
      setMessage("Please fill in all required fields.")
      return
    }

    // Email format check
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setMessage("Please enter a valid email address.")
      return
    }

    // LinkedIn URL check (optional)
    if (
      linkedin &&
      !/^https?:\/\/([a-z]{2,3}\.)?linkedin\.com\/.*$/i.test(linkedin)
    ) {
      setMessage("Please enter a valid LinkedIn profile URL.")
      return
    }

    setSubmitting(true)
    const formData = new FormData()
    formData.append("first_name", sanitizeText(firstName))
    formData.append("last_name", sanitizeText(lastName))
    formData.append("email", sanitizeText(email))
    formData.append("phone", sanitizeText(phone))
    formData.append("linkedin", sanitizeUrl(linkedin))
    if (fileInputRef.current.files && fileInputRef.current.files.length > 0) {
      formData.append("file", fileInputRef.current.files[0])
    }

    try {
      const res = await fetch("/api/career-submit.php", {
        method: "POST",
        body: formData,
      })
      const text = await res.text()
      if (res.ok && text.includes("Mail sent successfully")) {
        setMessage("Application submitted successfully!")
        clearForm()
      } else {
        setMessage("Submission failed. Please try again later.")
      }
    } catch {
      setMessage("Submission failed. Please try again later.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form
      className="w-full bg-white  p-6 xl:p-16 flex flex-col gap-6 border border-[--color-gray] h-fit"
      encType="multipart/form-data"
      onSubmit={handleSubmit}
      autoComplete="off"
    >
      <Title className="xl:text-[48px]" title="Join Us" type="h2" />
      {/* First and Last Name */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <label
            htmlFor="firstName"
            className="block  lg:text-[24px] text-gray-700 mb-2"
          >
            First Name
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="w-full border xl:text-[24px]  border-gray-300  px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-400"
          />
        </div>
        <div className="flex-1">
          <label
            htmlFor="lastName"
            className="block  lg:text-[24px] text-gray-700 mb-2"
          >
            Last Name
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            required
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="w-full border xl:text-[24px] border-gray-300  px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-400"
          />
        </div>
      </div>
      {/* Email Address */}
      <div>
        <label
          htmlFor="email"
          className="block  lg:text-[24px] text-gray-700 mb-2"
        >
          Email Address
        </label>
        <input
          type="email"
          id="email"
          name="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full border xl:text-[24px] border-gray-300  px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-400"
        />
      </div>
      {/* Phone */}
      <div>
        <label
          htmlFor="phone"
          className="block  lg:text-[24px] text-gray-700 mb-2"
        >
          Phone
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          required
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="w-full border xl:text-[24px] border-gray-300  px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-400"
        />
      </div>
      {/* LinkedIn Profile */}
      <div>
        <label
          htmlFor="linkedin"
          className="block  lg:text-[24px] text-gray-700 mb-2"
        >
          LinkedIn Profile
        </label>
        <input
          type="url"
          id="linkedin"
          name="linkedin"
          placeholder="https://linkedin.com/in/yourprofile"
          value={linkedin}
          onChange={(e) => setLinkedin(e.target.value)}
          className="w-full border xl:text-[24px] border-gray-300  px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-400"
        />
      </div>
      {/* File Upload with Drag and Drop */}
      <div>
        <label className="block  lg:text-[24px] text-gray-700 mb-2">
          Resume
        </label>
        <div
          className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300  px-4 py-8 cursor-pointer hover:border-red-400 transition"
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <svg
            className="w-8 h-8 text-gray-400 mb-2"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M7 16v-4a4 4 0 018 0v4m-4 4v-4m0 0V4m0 12a4 4 0 01-4-4V4a4 4 0 018 0v8a4 4 0 01-4 4z"
            />
          </svg>
          <span className="text-gray-500  mb-2">
            Drag & drop your file here, or{" "}
            <span className="text-[--color-brand] underline">browse</span>
          </span>
          <input
            id="fileUpload"
            name="attachment"
            type="file"
            className="hidden"
            required
            ref={fileInputRef}
            onChange={handleFileChange}
          />
        </div>
      </div>
      {/* Show selected file name */}
      {resumeFileName && (
        <div className="text-gray-700 text-sm xl:text-base mb-2">
          <span className="font-semibold">Selected file:</span> {resumeFileName}
        </div>
      )}
      {/* Submit Button */}
      <div>
        <button
          type="submit"
          className="bg-[--color-brand] hover:bg-[--color-danger] uppercase font-oswald text-[--color-dark]  text-white flex w-full xl:w-fit items-center justify-center gap-4 text-[16px] 2  3xl:text-[38px]  py-5 px-5 3xl:px-16 transition-all duration-300 ease-in-out"
          disabled={submitting}
        >
          {submitting ? "Submitting..." : "Submit Application"}
        </button>
      </div>
      {/* Submission message */}
      {message && (
        <div
          className="mt-4 p-3 rounded bg-gray-100 text-gray-800 border border-gray-300 cursor-pointer"
          onClick={() => setMessage(null)}
        >
          {message}
        </div>
      )}
    </form>
  )
}
